import { deriveClipPublishState } from '@src/services/publish/clipCardPublishState';
import { DEFAULT_CLIP_REVIEW_STATUS } from '@src/types.ts/video';
import {
  getLastActionEditor,
  getLastActionTimestamp,
} from '@src/utility/lastAction';
import { getMostRecentPublishInfo } from '@src/utility/publishInfo';
import { getFormattedDurationForStoryVideo } from '@src/utility/timeFormat';
import { useEffect, useMemo, useState } from 'react';

import type { OtherVideo } from '../../types.ts/story';
import type { ClipPublishRecordsLookup } from './hooks/useStoryClipPublishRecords';
import type { ClipReviewStatus } from '@src/types.ts/video';

/** Sentinel value used in the assignee filter for clips with no assignee. */
export const UNASSIGNED_FILTER_VALUE = '';

export type FilterState = {
  // No clipType here: the Clips / Reels tabs above the grid own that split
  // (PLAT-11249, superseding PLAT-11128's Type filter).
  clipStatus: {
    published: boolean;
    draft: boolean;
  };
  clipLength: {
    over240s: boolean;
    s120to240s: boolean;
    s60to120s: boolean;
    s30to60s: boolean;
    lessThan30s: boolean;
  };
  creators: Set<string>;
  reviewStatus: Set<ClipReviewStatus>;
  assignees: Set<string>;
};

const initialFilterState: FilterState = {
  clipStatus: {
    published: false,
    draft: false,
  },
  clipLength: {
    over240s: false,
    s120to240s: false,
    s60to120s: false,
    s30to60s: false,
    lessThan30s: false,
  },
  creators: new Set<string>(),
  reviewStatus: new Set<ClipReviewStatus>(),
  assignees: new Set<string>(),
};

export function useSocialClipsFilter(
  videos: OtherVideo[] | undefined,
  /**
   * The grid's publish records, so the Published/Draft filter, the recency sort
   * and the creator facet see a dubbed publish. Without them a clip published
   * only in a translation shows a live badge on its card yet disappears under
   * the Published filter — a worse answer than being uniformly wrong
   * (PLAT-11591).
   */
  publishRecordsFor: ClipPublishRecordsLookup,
  originalLanguage: string,
) {
  const [filters, setFilters] = useState<FilterState>(initialFilterState);

  // Union of the legacy `lastPublishJson` entries and the clip's publish
  // records, which is where a translated publish is recorded.
  const publishInfoFor = (video: OtherVideo) =>
    deriveClipPublishState(video, publishRecordsFor, originalLanguage)
      .publishInfo;

  // Extract unique creators from videos dynamically
  const availableCreators = useMemo(() => {
    if (!videos) return [];
    const creators = new Set<string>();
    videos.forEach(video => {
      // Check for publisher from published clips
      const lastPublishInfo = publishInfoFor(video);
      const mostRecent = getMostRecentPublishInfo(lastPublishInfo);
      if (mostRecent?.publisher) {
        creators.add(mostRecent.publisher);
      }

      // Check for editor from draft clips (lastActionJson)
      const editor = getLastActionEditor(video);
      if (editor) {
        creators.add(editor);
      }
    });
    return Array.from(creators).sort();
  }, [videos, publishRecordsFor, originalLanguage]);

  // Extract assignee emails present on the clips. Includes the "Unassigned"
  // sentinel when at least one clip has no assignee.
  const availableAssignees = useMemo(() => {
    if (!videos) return [];
    const assignees = new Set<string>();
    let hasUnassigned = false;
    videos.forEach(video => {
      if (video.assignee) {
        assignees.add(video.assignee);
      } else {
        hasUnassigned = true;
      }
    });
    const sorted = Array.from(assignees).sort();
    return hasUnassigned ? [UNASSIGNED_FILTER_VALUE, ...sorted] : sorted;
  }, [videos]);

  // Drop selected assignees that are no longer present on any clip.
  useEffect(() => {
    if (filters.assignees.size === 0) return;
    const availableSet = new Set(availableAssignees);
    const hasStale = Array.from(filters.assignees).some(
      assignee => !availableSet.has(assignee),
    );
    if (hasStale) {
      setFilters(prev => ({
        ...prev,
        assignees: new Set(
          Array.from(prev.assignees).filter(assignee =>
            availableSet.has(assignee),
          ),
        ),
      }));
    }
  }, [availableAssignees, filters.assignees]);

  // Clean up filters.creators when availableCreators changes
  // Remove any selected creators that are no longer available
  useEffect(() => {
    if (filters.creators.size === 0) return;

    const availableCreatorsSet = new Set(availableCreators);
    const hasStaleCreators = Array.from(filters.creators).some(
      creator => !availableCreatorsSet.has(creator),
    );

    if (hasStaleCreators) {
      setFilters(prev => ({
        ...prev,
        creators: new Set(
          Array.from(prev.creators).filter(creator =>
            availableCreatorsSet.has(creator),
          ),
        ),
      }));
    }
  }, [availableCreators, filters.creators]);

  // Helper function to get the most recent publish timestamp for sorting
  const getMostRecentPublishTimestamp = (video: OtherVideo): number => {
    const publishInfo = publishInfoFor(video);
    const mostRecent = getMostRecentPublishInfo(publishInfo);
    return mostRecent?.timestamp ? new Date(mostRecent.timestamp).getTime() : 0;
  };

  // Sort videos by most recent activity (publish date or last save date, whichever is later)
  const sortedVideos = useMemo(() => {
    if (!videos) return [];
    return [...videos].sort((a, b) => {
      const aPublishTime = getMostRecentPublishTimestamp(a);
      const bPublishTime = getMostRecentPublishTimestamp(b);
      const aActionTime = getLastActionTimestamp(a);
      const bActionTime = getLastActionTimestamp(b);
      const aTime = Math.max(aPublishTime, aActionTime);
      const bTime = Math.max(bPublishTime, bActionTime);
      // Sort descending (most recent first)
      return bTime - aTime;
    });
  }, [videos, publishRecordsFor, originalLanguage]);

  // Filter videos based on selected filters
  const filteredVideos = useMemo(() => {
    if (!sortedVideos.length) return [];

    // If no filters are active, return all videos (already sorted)
    const hasActiveFilters =
      filters.clipStatus.published ||
      filters.clipStatus.draft ||
      filters.clipLength.over240s ||
      filters.clipLength.s120to240s ||
      filters.clipLength.s60to120s ||
      filters.clipLength.s30to60s ||
      filters.clipLength.lessThan30s ||
      filters.creators.size > 0 ||
      filters.reviewStatus.size > 0 ||
      filters.assignees.size > 0;

    if (!hasActiveFilters) {
      return sortedVideos;
    }

    return sortedVideos.filter((video: OtherVideo) => {
      // Filter by clip status (published/draft)
      const hasStatusFilter =
        filters.clipStatus.published || filters.clipStatus.draft;
      if (hasStatusFilter) {
        const isPublished = publishInfoFor(video).length > 0;
        if (filters.clipStatus.published && !filters.clipStatus.draft) {
          if (!isPublished) return false;
        } else if (filters.clipStatus.draft && !filters.clipStatus.published) {
          if (isPublished) return false;
        }
        // If both are selected, show all (no filtering needed)
      }

      // Filter by clip length
      const hasLengthFilter =
        filters.clipLength.over240s ||
        filters.clipLength.s120to240s ||
        filters.clipLength.s60to120s ||
        filters.clipLength.s30to60s ||
        filters.clipLength.lessThan30s;

      if (hasLengthFilter) {
        const duration = getFormattedDurationForStoryVideo(video).duration ?? 0;

        let matchesLength = false;

        if (filters.clipLength.over240s && duration >= 240) {
          matchesLength = true;
        }
        if (
          filters.clipLength.s120to240s &&
          duration >= 120 &&
          duration < 240
        ) {
          matchesLength = true;
        }
        if (filters.clipLength.s60to120s && duration >= 60 && duration < 120) {
          matchesLength = true;
        }
        if (filters.clipLength.s30to60s && duration >= 30 && duration < 60) {
          matchesLength = true;
        }
        if (filters.clipLength.lessThan30s && duration < 30) {
          matchesLength = true;
        }

        if (!matchesLength) {
          return false;
        }
      }

      // Filter by creators (check both publisher for published and editor for drafts)
      if (filters.creators.size > 0) {
        const lastPublishInfo = publishInfoFor(video);
        const mostRecent = getMostRecentPublishInfo(lastPublishInfo);
        const editor = getLastActionEditor(video);
        const matchesCreator =
          (!!mostRecent?.publisher &&
            filters.creators.has(mostRecent.publisher)) ||
          (!!editor && filters.creators.has(editor));
        if (!matchesCreator) return false;
      }

      // Filter by review status (unset status counts as the default).
      if (filters.reviewStatus.size > 0) {
        const status = video.reviewStatus || DEFAULT_CLIP_REVIEW_STATUS;
        if (!filters.reviewStatus.has(status)) return false;
      }

      // Filter by assignee (empty assignee counts as "Unassigned").
      if (filters.assignees.size > 0) {
        const assignee = video.assignee || UNASSIGNED_FILTER_VALUE;
        if (!filters.assignees.has(assignee)) return false;
      }

      return true;
    });
  }, [
    sortedVideos,
    filters.clipStatus.published,
    filters.clipStatus.draft,
    filters.clipLength.over240s,
    filters.clipLength.s120to240s,
    filters.clipLength.s60to120s,
    filters.clipLength.s30to60s,
    filters.clipLength.lessThan30s,
    filters.creators,
    filters.reviewStatus,
    filters.assignees,
    publishRecordsFor,
    originalLanguage,
  ]);

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.clipStatus.published) count++;
    if (filters.clipStatus.draft) count++;
    if (filters.clipLength.over240s) count++;
    if (filters.clipLength.s120to240s) count++;
    if (filters.clipLength.s60to120s) count++;
    if (filters.clipLength.s30to60s) count++;
    if (filters.clipLength.lessThan30s) count++;
    count += filters.creators.size;
    count += filters.reviewStatus.size;
    count += filters.assignees.size;
    return count;
  }, [filters]);

  // Toggle filter handlers
  const toggleClipStatusFilter = (status: 'published' | 'draft') => {
    setFilters(prev => ({
      ...prev,
      clipStatus: {
        ...prev.clipStatus,
        [status]: !prev.clipStatus[status],
      },
    }));
  };

  const toggleClipLengthFilter = (
    length:
      | 'over240s'
      | 's120to240s'
      | 's60to120s'
      | 's30to60s'
      | 'lessThan30s',
  ) => {
    setFilters(prev => ({
      ...prev,
      clipLength: {
        ...prev.clipLength,
        [length]: !prev.clipLength[length],
      },
    }));
  };

  const toggleCreatorFilter = (creator: string) => {
    setFilters(prev => {
      const newCreators = new Set(prev.creators);
      if (newCreators.has(creator)) {
        newCreators.delete(creator);
      } else {
        newCreators.add(creator);
      }
      return {
        ...prev,
        creators: newCreators,
      };
    });
  };

  const toggleReviewStatusFilter = (status: ClipReviewStatus) => {
    setFilters(prev => {
      const next = new Set(prev.reviewStatus);
      if (next.has(status)) {
        next.delete(status);
      } else {
        next.add(status);
      }
      return { ...prev, reviewStatus: next };
    });
  };

  const toggleAssigneeFilter = (assignee: string) => {
    setFilters(prev => {
      const next = new Set(prev.assignees);
      if (next.has(assignee)) {
        next.delete(assignee);
      } else {
        next.add(assignee);
      }
      return { ...prev, assignees: next };
    });
  };

  return {
    filters,
    filteredVideos,
    availableCreators,
    availableAssignees,
    activeFilterCount,
    toggleClipStatusFilter,
    toggleClipLengthFilter,
    toggleCreatorFilter,
    toggleReviewStatusFilter,
    toggleAssigneeFilter,
  };
}
